import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PortableText } from "@portabletext/react";
import { Tag, Calendar, Clock } from "lucide-react";
import { sanityClient, urlFor } from "@/lib/sanity";
import {
  useLocale,
  pickLocalized,
  pickLocalizedRaw,
  tr,
  t,
  formatDate,
  DEFAULT_LOCALE,
} from "@/lib/i18n";
import { portableComponents } from "@/lib/portable-text";
import {
  pageMeta,
  ogImageUrl,
  articleLd,
  breadcrumbLd,
  JsonLd,
  excerptFromBlocks,
  wordCountFromBlocks,
} from "@/lib/seo";

const QUERY = `*[_type == "blogPost" && (slug.en.current == $slug || slug.fr.current == $slug)][0]{
  _id, _updatedAt, title, excerpt, seo, coverImage, coverImageCredit, category,
  publishedAt, readingTime, content, sources, slug
}`;

/** Authored SEO description, else the excerpt, else the opening of the English body, else the default. */
function metaDescription(post: any): string {
  return (
    pickLocalized(post?.seo?.metaDescription, DEFAULT_LOCALE) ||
    pickLocalized(post?.excerpt, DEFAULT_LOCALE) ||
    excerptFromBlocks(pickLocalizedRaw<any[]>(post?.content, DEFAULT_LOCALE)) ||
    "Slow reads for busy minds."
  );
}

export const Route = createFileRoute("/blog/$slug")({
  component: BlogPostPage,
  loader: async ({ params }) => {
    const post = await sanityClient.fetch<any>(QUERY, { slug: params.slug });
    if (!post) throw notFound();
    return post;
  },
  head: ({ loaderData: post, params }) =>
    pageMeta({
      title: post
        ? pickLocalized(post.seo?.metaTitle, DEFAULT_LOCALE) ||
          `${pickLocalized(post.title, DEFAULT_LOCALE)} — Matchamind`
        : "Blog — Matchamind",
      description: metaDescription(post),
      // Canonicalize to the English slug so /blog/<fr-slug> doesn't self-canonical into a duplicate.
      path: `/blog/${post?.slug?.en?.current ?? params.slug}`,
      ogImage: ogImageUrl(post?.seo?.ogImage ?? post?.coverImage),
      ogImageAlt:
        pickLocalized(post?.coverImage?.alt, DEFAULT_LOCALE) ||
        pickLocalized(post?.title, DEFAULT_LOCALE) ||
        undefined,
      ogType: "article",
      publishedTime: post?.publishedAt,
      modifiedTime: post?._updatedAt,
    }),
});

const CATEGORY_LABELS: Record<string, { en: string; fr: string }> = {
  insights: { en: "Insights", fr: "Réflexions" },
  becoming: { en: "Becoming", fr: "Devenir" },
  "self-talk": { en: "Self-Talk", fr: "Discours intérieur" },
  regulation: { en: "Regulation", fr: "Régulation" },
};

function BlogPostPage() {
  const { slug } = Route.useParams();
  const locale = useLocale();
  const search = locale === "en" ? {} : { lang: "fr" as const };
  const post = Route.useLoaderData();

  const body = pickLocalizedRaw<any[]>(post.content, locale);
  // Structured data always describes the English page — the FR slug canonicals to it.
  const canonicalPath = `/blog/${post.slug?.en?.current ?? slug}`;
  const categoryKey = (post.category ?? "").toLowerCase();
  const categoryLabel = CATEGORY_LABELS[categoryKey]
    ? tr(CATEGORY_LABELS[categoryKey], locale)
    : post.category;

  return (
    <article className="flex w-full flex-col items-center gap-8 pb-16">
      <JsonLd
        data={articleLd({
          title: post.title,
          description: metaDescription(post),
          path: canonicalPath,
          image: ogImageUrl(post.coverImage),
          datePublished: post.publishedAt,
          dateModified: post._updatedAt,
          section: CATEGORY_LABELS[categoryKey]?.en ?? post.category,
          wordCount: wordCountFromBlocks(pickLocalizedRaw<any[]>(post.content, DEFAULT_LOCALE)),
          readingTimeMinutes: post.readingTime,
          sources: post.sources,
          collection: "blog",
        })}
      />
      <JsonLd
        data={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog" },
          { name: pickLocalized(post.title, DEFAULT_LOCALE), path: canonicalPath },
        ])}
      />
      {/* Title + meta header */}
      <header className="mx-auto flex w-full max-w-[1200px] flex-col items-start justify-between gap-6 px-6 py-8 md:flex-row md:items-start md:gap-12 md:px-10 md:py-12 lg:px-20">
        <div className="flex-1">
          <Link
            to="/blog"
            search={search}
            className="mb-4 inline-block text-sm text-text-muted hover:text-text-primary"
          >
            ← {tr(t.common.backToBlog, locale)}
          </Link>
          <h1 className="font-display text-[28px] font-semibold leading-[36px] tracking-[0.2px] text-text-primary md:text-[34px] md:leading-[44px]">
            {pickLocalized(post.title, locale)}
          </h1>
        </div>
        <div className="flex flex-col gap-2 md:items-start md:pt-[48px]">
          {categoryLabel && (
            <div className="flex items-center gap-2 text-text-muted">
              <Tag className="h-4 w-4" />
              <span className="font-sans text-[14px] leading-5">{categoryLabel}</span>
            </div>
          )}
          {post.publishedAt && (
            <div className="flex items-center gap-2 text-text-muted">
              <Calendar className="h-4 w-4" />
              <span className="font-sans text-[14px] leading-5">
                {formatDate(post.publishedAt, locale)}
              </span>
            </div>
          )}
          {post.readingTime && (
            <div className="flex items-center gap-2 text-text-muted">
              <Clock className="h-4 w-4" />
              <span className="font-sans text-[14px] leading-5">{post.readingTime} min</span>
            </div>
          )}
        </div>
      </header>

      {/* Full-bleed cover image */}
      {post.coverImage && (
        <figure className="w-full">
          <img
            src={urlFor(post.coverImage).width(1600).height(672).fit("crop").url()}
            alt={pickLocalized(post.coverImage?.alt, locale) || pickLocalized(post.title, locale)}
            className="h-[220px] w-full object-cover md:h-[336px]"
          />
          {post.coverImageCredit && (
            <figcaption className="mx-auto mt-2 max-w-[840px] px-6 text-xs text-text-muted">
              {post.coverImageCredit}
            </figcaption>
          )}
        </figure>
      )}

      {/* Excerpt / intro */}
      {post.excerpt && (
        <div className="mx-auto w-full max-w-[840px] px-6 md:px-10">
          <p className="text-justify font-sans text-[16px] font-light leading-[22px] text-text-secondary">
            {pickLocalized(post.excerpt, locale)}
          </p>
        </div>
      )}

      {/* Body */}
      <div className="mx-auto w-full max-w-[840px] px-6 md:px-10">
        {body && <PortableText value={body} components={portableComponents as any} />}
      </div>

      {post.sources?.length > 0 && (
        <section className="mx-auto w-full max-w-[840px] px-6 md:px-10">
          <div className="mt-8 border-t border-primary-100 pt-8">
            <h2 className="font-display text-[20px] font-semibold text-text-primary">Sources</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm text-text-muted">
              {post.sources.map((s: any, i: number) => (
                <li key={i}>
                  {s.author && <span>{s.author}</span>}
                  {s.year && <span> ({s.year})</span>}
                  {(s.author || s.year) && <span>. </span>}
                  {s.title && (
                    <em>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noreferrer" className="underline">
                          {s.title}
                        </a>
                      ) : (
                        s.title
                      )}
                    </em>
                  )}
                  {s.journal && <span>. {s.journal}</span>}
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
    </article>
  );
}
